import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Briefcase,
  Calendar,
  IndianRupee,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  AlertTriangle,
  X,
  TrendingUp,
  History,
  Plus
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/dateUtils';

const Employee360FestivalAdvance = ({ employeeId }) => {
  const { hasPermission } = useAuth();
  const [applications, setApplications] = useState([]);
  const [recoveryHistory, setRecoveryHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);

  // Request creation state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [festivals, setFestivals] = useState([]);
  const [selectedFestivalId, setSelectedFestivalId] = useState('');
  const [remarks, setRemarks] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [eligibility, setEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);

  useEffect(() => {
    fetchApplications();
    fetchRecoveryHistory();
  }, [employeeId]);

  // Component-level permission check
  if (!hasPermission('emp:fadv:create') && !hasPermission('emp:fadv:approve') && !hasPermission('emp:fadv:reject') && !hasPermission('*')) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-center space-x-2">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <span>You do not have permission to view festival advance details.</span>
      </div>
    );
  }

  const fetchFestivals = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/v1/festival-advance/festivals?is_active=true', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFestivals(response.data || []);
    } catch (err) {
      console.error('Error fetching active festivals:', err);
    }
  };

  const checkEligibility = async (festivalId) => {
    if (!festivalId) {
      setEligibility(null);
      return;
    }
    setCheckingEligibility(true);
    setCreateError('');
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/v1/festival-advance/my-eligibility/${festivalId}?employee_id=${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEligibility(response.data);
    } catch (err) {
      console.error('Error checking eligibility:', err);
      setCreateError(err.response?.data?.detail || 'Failed to check eligibility');
      setEligibility(null);
    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleOpenCreateModal = () => {
    fetchFestivals();
    setSelectedFestivalId('');
    setRemarks('');
    setEligibility(null);
    setCreateError('');
    setShowCreateModal(true);
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    if (!selectedFestivalId) return;
    setCreateSubmitting(true);
    setCreateError('');
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`/v1/festival-advance/employee/${employeeId}/application`, {
        festival_id: parseInt(selectedFestivalId),
        remarks: remarks.trim() || undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateModal(false);
      fetchApplications();
      fetchRecoveryHistory();
    } catch (err) {
      console.error('Error creating request:', err);
      let errMsg = 'Failed to create request';
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errMsg = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errMsg = err.response.data.detail.map(e => e.msg || e).join(', ');
        }
      }
      setCreateError(errMsg);
    } finally {
      setCreateSubmitting(false);
    }
  };


  const fetchApplications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/v1/festival-advance/applications?employee_id=${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data.applications || response.data || []);
    } catch (err) {
      setError('Failed to load festival advance data');
      console.error('Error fetching festival advance:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedule = async (applicationId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/v1/festival-advance/applications/${applicationId}/schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedApplication(prev => ({ ...prev, schedule: response.data }));
      setShowSchedule(true);
    } catch (err) {
      console.error('Error fetching schedule:', err);
    }
  };

  const fetchRecoveryHistory = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`v1/festival-advance/recoveries?employee_id=${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecoveryHistory(response.data.recoveries || response.data || []);
    } catch (err) {
      console.error('Error fetching recovery history:', err);
    }
  };

  const handleApprove = async (applicationId) => {
    const remarks = window.prompt("Enter approval remarks (optional):");
    if (remarks === null) return; // user cancelled
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`/v1/festival-advance/applications/${applicationId}/approve`, 
        { approval_remarks: remarks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchApplications();
    } catch (err) {
      console.error('Error approving application:', err);
      alert(err.response?.data?.detail || 'Failed to approve application');
    }
  };

  const handleReject = async (applicationId) => {
    const reason = window.prompt("Enter rejection reason (required):");
    if (reason === null) return; // user cancelled
    if (!reason.trim()) {
      alert("Rejection reason is required.");
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`/v1/festival-advance/applications/${applicationId}/reject`, 
        { rejection_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchApplications();
    } catch (err) {
      console.error('Error rejecting application:', err);
      alert(err.response?.data?.detail || 'Failed to reject application');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'rejected':
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  const activeAdvance = applications.find(a => a.status === 'active');

  return (
    <div className="space-y-6">
      {/* Current Active Advance */}
      {activeAdvance && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-blue-900 flex items-center">
              <Briefcase className="w-5 h-5 mr-2" />
              Active Festival Advance
            </h3>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(activeAdvance.status)}`}>
              {activeAdvance.status}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-blue-700">Festival</p>
              <p className="font-semibold text-blue-900">{activeAdvance.festival?.festival_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-blue-700">Advance Amount</p>
              <p className="font-semibold text-blue-900">₹{activeAdvance.advance_amount?.toLocaleString() || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-blue-700">Recovered</p>
              <p className="font-semibold text-blue-900">₹{activeAdvance.amount_recovered?.toLocaleString() || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-blue-700">Outstanding</p>
              <p className="font-semibold text-blue-900">₹{activeAdvance.outstanding_balance?.toLocaleString() || '-'}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            <div>
              <p className="text-sm text-blue-700">Monthly Installment</p>
              <p className="font-semibold text-blue-900">₹{activeAdvance.monthly_installment?.toLocaleString() || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-blue-700">Completed Installments</p>
              <p className="font-semibold text-blue-900">{activeAdvance.completed_installments || 0} / {activeAdvance.total_installments || 10}</p>
            </div>
            <div>
              <p className="text-sm text-blue-700">Remaining Installments</p>
              <p className="font-semibold text-blue-900">{activeAdvance.remaining_installments || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-blue-700">Recovery Start Date</p>
              <p className="font-semibold text-blue-900">{formatDate(activeAdvance.recovery_start_date, '-')}</p>
            </div>
          </div>

          {/* Recovery Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-blue-700">Recovery Progress</span>
              <span className="font-semibold text-blue-900">{Math.round((activeAdvance.completed_installments || 0) / (activeAdvance.total_installments || 10) * 100)}%</span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.round((activeAdvance.completed_installments || 0) / (activeAdvance.total_installments || 10) * 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Applications History */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-gray-600" />
              Application History
            </h3>
            <p className="text-sm text-gray-500 mt-1">All festival advance applications for this employee</p>
          </div>
          <button
            onClick={handleOpenCreateModal}
            className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold text-xs shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Create Request
          </button>
        </div>

        {applications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No festival advance applications found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left">Application No.</th>
                  <th className="px-4 py-3 text-left">Festival</th>
                  <th className="px-4 py-3 text-left">Amount</th>
                  <th className="px-4 py-3 text-left">Installments</th>
                  <th className="px-4 py-3 text-left">Application Date</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Outstanding</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {applications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 font-medium text-gray-900">{app.application_number}</td>
                    <td className="px-4 py-4 text-gray-700">{app.festival?.festival_name || '-'}</td>
                    <td className="px-4 py-4 text-gray-700">₹{app.advance_amount?.toLocaleString() || '-'}</td>
                    <td className="px-4 py-4 text-gray-700">{app.requested_installments || '-'}</td>
                    <td className="px-4 py-4 text-gray-700">{formatDate(app.application_date, '-')}</td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(app.status)}`}>
                        {app.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-gray-700">₹{app.outstanding_balance?.toLocaleString() || '-'}</td>
                    <td className="px-4 py-4 text-center">
                      {app.status === 'pending' ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleApprove(app.id)}
                            className="px-3 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(app.id)}
                            className="px-3 py-1 bg-rose-600 text-white rounded text-xs font-medium hover:bg-rose-700"
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payroll Recovery History */}
      {recoveryHistory.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-gray-600" />
              Payroll Recovery History
            </h3>
            <p className="text-sm text-gray-500 mt-1">Festival advance deductions from payroll</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left">Recovery Number</th>
                  <th className="px-4 py-3 text-left">Application</th>
                  <th className="px-4 py-3 text-left">Installment</th>
                  <th className="px-4 py-3 text-left">Recovery Date</th>
                  <th className="px-4 py-3 text-left">Amount</th>
                  <th className="px-4 py-3 text-left">Balance Before</th>
                  <th className="px-4 py-3 text-left">Balance After</th>
                  <th className="px-4 py-3 text-left">Payroll Period</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recoveryHistory.map((recovery) => (
                  <tr key={recovery.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 font-medium text-gray-900">{recovery.recovery_number}</td>
                    <td className="px-4 py-4 text-gray-700">{recovery.application_number || '-'}</td>
                    <td className="px-4 py-4 text-gray-700">{recovery.installment_number}</td>
                    <td className="px-4 py-4 text-gray-700">{formatDate(recovery.recovery_date, '-')}</td>
                    <td className="px-4 py-4 text-gray-700">₹{recovery.recovery_amount?.toLocaleString()}</td>
                    <td className="px-4 py-4 text-gray-700">₹{recovery.balance_before?.toLocaleString()}</td>
                    <td className="px-4 py-4 text-gray-700">₹{recovery.balance_after?.toLocaleString()}</td>
                    <td className="px-4 py-4 text-gray-700">{recovery.payroll_month}/{recovery.payroll_year}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showSchedule && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Recovery Schedule</h3>
              <button
                onClick={() => setShowSchedule(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {selectedApplication.schedule && selectedApplication.schedule.schedule_details ? (
                <div className="space-y-3">
                  {selectedApplication.schedule.schedule_details.map((detail) => (
                    <div
                      key={detail.id}
                      className={`p-4 rounded-lg border ${
                        detail.is_recovered
                          ? 'bg-green-50 border-green-200'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium text-gray-900">
                            Installment {detail.installment_number}
                          </p>
                          <p className="text-sm text-gray-600">
                            Due: {formatDate(detail.due_date, '-')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            ₹{detail.due_amount?.toLocaleString()}
                          </p>
                          {detail.is_recovered && (
                            <p className="text-sm text-green-600">
                              Recovered: {formatDate(detail.recovered_date, '-')}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No schedule available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Request Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden border border-gray-150 text-left">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900">Create Festival Advance Request</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1.5 hover:bg-gray-200 rounded-lg transition text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateRequest}>
              <div className="p-6 space-y-4">
                {createError && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span>{createError}</span>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee
                  </label>
                  <input
                    type="text"
                    value={applications[0]?.employee?.full_name || `Employee ID: ${employeeId}`}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 font-medium"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Active Festival
                  </label>
                  <select
                    value={selectedFestivalId}
                    onChange={(e) => {
                      setSelectedFestivalId(e.target.value);
                      checkEligibility(e.target.value);
                    }}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700"
                  >
                    <option value="">Select a festival...</option>
                    {festivals.map(fest => (
                      <option key={fest.id} value={fest.id}>{fest.festival_name} ({fest.festival_code})</option>
                    ))}
                  </select>
                </div>

                {checkingEligibility && (
                  <div className="text-sm text-blue-600 flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>Checking eligibility...</span>
                  </div>
                )}

                {eligibility && (
                  <div className={`p-4 rounded-lg border ${eligibility.is_eligible ? 'bg-green-50 border-green-200 text-green-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
                    <p className="font-semibold text-sm">{eligibility.is_eligible ? 'Employee is Eligible' : 'Employee is NOT Eligible'}</p>
                    <p className="text-xs mt-1">{eligibility.reason}</p>
                  </div>
                )}

                {eligibility?.is_eligible && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-0.5">
                          Advance Amount
                        </label>
                        <input
                          type="text"
                          value={`₹${(eligibility.projected_advance_amount || 12500).toLocaleString()}`}
                          disabled
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 font-semibold"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-0.5">
                          Installments
                        </label>
                        <input
                          type="text"
                          value={`${eligibility.projected_installments || 10} Months`}
                          disabled
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 font-semibold"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Remarks
                      </label>
                      <textarea
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Any additional remarks..."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition text-gray-700 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createSubmitting || !selectedFestivalId || !eligibility?.is_eligible}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 text-sm font-semibold shadow-sm"
                >
                  {createSubmitting ? 'Creating...' : 'Create Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employee360FestivalAdvance;
